import { Injectable } from '@angular/core';
import { SettingsProviderService } from './providers/settings.provider.service';
import { DeployParams } from '../deployParams';
import { ConstantValues } from '../models/util/constantValues';
import { DynamicTranslations } from 'src/locale/dynamicTranslations';
import { map, switchMap } from 'rxjs/operators';
import { ModelMapperService } from './util/model.mapper.service';
import { Subject, Observable, of, forkJoin } from 'rxjs';
import { LocaleHelper } from 'src/locale/LocaleHelper';
import { IBetTypeSelection } from '../models/betType/betTypeSelection.interface';
import { IBetTypeOutcome } from '../models/betType/betTypeOutcome.interface';
import { IBetTypeShowType } from '../models/betType/betTypeShowType.interface';
import { QueryParamsService } from './http/queryParams.service';
import { StorageService } from './util/storage.service';
import { IBetTypesShowTypesTO } from '../models/betType/transportObjects/betTypeShowTypesTO.interface';
import { UtilityService } from './util/utility.service';
import { IOutcomeCodeValue } from '../models/betType/outcomeCodeValue.interface';
import { IBusinessModel } from '../models/settings/businessModel.interface';
import { SBVDisplayType } from '../models/enums';
import { ViewConfigurations } from '../models/util/viewConfigurations';
import { environment } from 'src/environments/environment';
import { IBusinessModelWithAdditionalSettingsTO } from '../models/settings/transportObjects/businessModelTO.interface';

@Injectable()
export class SettingsService {
  private languageCulture: string;
  private betTypeShowTypes: { [betTypeId: number]: IBetTypeShowType } = null;
  private eventIdsForNotifications: number[] = [];
  private outcomeCodeValues: IOutcomeCodeValue[]  = null;

  private constantValuesLoadedSource = new Subject<void>();
  public constantValuesLoaded$ = this.constantValuesLoadedSource.asObservable();

  constructor(
    private settingsProvider: SettingsProviderService,
    private modelMapper: ModelMapperService,
    private queryParamsService: QueryParamsService,
    private storage: StorageService,
    private utilityService: UtilityService) {
    ViewConfigurations.setValues();
    // this.storage.betTypeShowTypes = null;
    // const lang = this.userService.user.userLanguage;
    const lang = this.storage.language;
    this.languageCulture = LocaleHelper.cultureName[lang];
    if(!environment.isMaintinanceBreak){
      this.getSettings();
    }
  }

  get eventsForNotiFications(): number[]{
    return this.eventIdsForNotifications;
  }

  public reloadSettings(){
    this.eventIdsForNotifications = [];
    this.getSettings();
  }

  private getSettings(): void {

    const tmUser = this.storage.userTM;

   // get events for notifications from server
    const productVersion = this.storage.productVersion;
    if (productVersion !== environment.productVersion) {
      this.storage.clearSessionStorage();
      this.storage.clearLocalStorage();
      this.storage.betTypeShowTypes = null;
      this.storage.multiwayRestrictions = null;
      this.storage.productVersion = environment.productVersion;
    }

    let bussinesModelRequest: Observable<IBusinessModel | IBusinessModelWithAdditionalSettingsTO>;
    if (tmUser && tmUser.loggedOffice) {
      bussinesModelRequest = this.settingsProvider.getBussinesModelTicketManager();
    } else {
      bussinesModelRequest = this.settingsProvider.getBussinesModel();
    }

    const currencyRequest = this.settingsProvider.getCurrency();

    forkJoin([bussinesModelRequest, currencyRequest]).pipe(
      switchMap(reqsponses => of({ bussinesModel: this.modelMapper.mapBussinesModel(reqsponses[0]), currency: this.modelMapper.mapCurrency(reqsponses[1]) }))
    ).subscribe(response => {
      ConstantValues.setBussinesModel(response.bussinesModel, this.utilityService.mobileView);
      ConstantValues.setCurrency(response.currency);
      this.constantValuesLoadedSource.next();
    });

    // this.settingsProvider.getBussinesModel().pipe(map(bm => this.modelMapper.mapBussinesModel(bm))).subscribe(model => ConstantValues.setBussinesModel(model));

    // this.settingsProvider.getCurrency().pipe(map(cur => this.modelMapper.mapCurrency(cur))).subscribe(curr => ConstantValues.setCurrency(curr));

    // NOT WORKING WITH ID OTHER THAN WEB
    // this.settingsProvider.getSuperBetSetting(tmUser && tmUser.loggedOffice ? tmUser.loggedOffice.id : environment.betWebOfficeId).pipe(map(sbs => this.modelMapper.mapSuperBetSettings(sbs))).subscribe(model => ConstantValues.setSuperBetSettings(model));
    this.settingsProvider.getSuperBetSetting(environment.betWebOfficeId).pipe(map(sbs => this.modelMapper.mapSuperBetSettings(sbs))).subscribe(model => ConstantValues.setSuperBetSettings(model));

    this.settingsProvider.getDynamicTranslations(this.languageCulture).subscribe(tran => DynamicTranslations.setAllTranslations(tran));

    this.settingsProvider.acceptanceTimeout().subscribe(timeout => ConstantValues.setAcceptanceTimeout(timeout.acceptanceTimeoutPM, timeout.acceptanceTimeoutLive));

    // // implement Cashing on provider if requesting for same languge
    // this.getBetTypeShowTypes().subscribe((x) => { });
  }

  getBetTypeShowTypes(): Observable<{ [betTypeId: number]: IBetTypeShowType }> {
    // console.log('settingsService GetBetTypeShowTypes');

    if (this.betTypeShowTypes) {
      // console.log('settingsService  GetBetTypeShowTypes return form local prop');
      return of(this.betTypeShowTypes);
    }

    const betTypeShowTypesFromStorage = this.storage.betTypeShowTypes;
    if (betTypeShowTypesFromStorage) {
      // console.log('settingsService  GetBetTypeShowTypes return from storage:', betTypeShowTypesFromStorage);
      this.betTypeShowTypes = betTypeShowTypesFromStorage;
      return of(this.betTypeShowTypes);
    }

    return this.settingsProvider.getTBetTypeShowTypes(this.languageCulture).pipe(
      switchMap((betTypeST) => {
        if (!this.betTypeShowTypes) {
          this.betTypeShowTypes = this.modelMapper.mapBetTypeShowTypes(betTypeST);
          // console.log('settingsService  GetBetTypeShowTypes return form server mapped:', this.betTypeShowTypes);
          this.storage.betTypeShowTypes = this.betTypeShowTypes;
        }
        return of(this.betTypeShowTypes);
      })
    );
  }

  getOutcomeCodeValues(): Observable<IOutcomeCodeValue[]> {
    if (this.outcomeCodeValues) {
      return of(this.storage.outcomeCodeValues);
    }

    const outcomeCodeValuesFromStorage = this.storage.outcomeCodeValues;
    if (outcomeCodeValuesFromStorage) {
      this.outcomeCodeValues = outcomeCodeValuesFromStorage;
      return of(this.outcomeCodeValues);
    }

    return this.settingsProvider.getOutcomeCodeValues().pipe(
      switchMap(vc => {
        this.outcomeCodeValues = this.modelMapper.mapOtcomeCodeValues(vc.outcomeCodeValues);
        this.storage.outcomeCodeValues = this.outcomeCodeValues;
        return of(this.outcomeCodeValues);
      }));
  }

  async getBetTypeOutcomesAndSbvDisplayTypeForSelection(selection: IBetTypeSelection): Promise<{betTypeOutcomes: IBetTypeOutcome[]; sbvDisplayType: SBVDisplayType}> {

    const btst = await this.getBetTypeShowTypes().toPromise();

    if (!btst[selection.betTypeId]) {
      return {betTypeOutcomes: [], sbvDisplayType: SBVDisplayType.NoSbv};
    }


    return {betTypeOutcomes: btst[selection.betTypeId].betTypeOutcomesList, sbvDisplayType: btst[selection.betTypeId].sbvDisplayType};
  }
}
